import { addHours, addMinutes, endOfMonth, endOfWeek, format, max, min, startOfMonth, startOfWeek } from "date-fns";
import { FunctionComponent, memo } from "react";
import { TimePeriodGroupBy } from "@edgetier/watchtower-types";
import { FormattedDate } from "@edgetier/client-components";
import { IProps } from "./phrase-counts-chart-tooltip.types";
import "./phrase-counts-chart-tooltip.scss";

/**
 * Tooltip of how many interactions matched the search phrase(s) at a given date and time.
 * @param props.dateTime   Date and time of reading.
 * @param props.proportion Show percentage or absolute values.
 * @param props.timePeriodGroup   Whether to show the time with the date or not.
 * @param props.value      Number of interactions.
 * @returns                Tooltip content.
 */
const PhraseCountsChartTooltip: FunctionComponent<IProps> = ({
    startDateTime,
    endDateTime,
    dateTime,
    proportion,
    timePeriodGroup,
    value,
}) => {
    return (
        <div className="phrase-counts-chart-tooltip">
            <div className="phrase-counts-chart-tooltip__date">
                {[TimePeriodGroupBy.Weeks, TimePeriodGroupBy.Months].some((group) => group !== timePeriodGroup) ? (
                    <>
                        {timePeriodGroup === TimePeriodGroupBy.Weeks && (
                            <>
                                <FormattedDate
                                    dateTime={max([startOfWeek(dateTime, { weekStartsOn: 1 }), startDateTime])}
                                />
                                &ndash;
                                <FormattedDate
                                    dateTime={min([endOfWeek(dateTime, { weekStartsOn: 1 }), endDateTime])}
                                />
                            </>
                        )}
                        {timePeriodGroup === TimePeriodGroupBy.Months && (
                            <>
                                <FormattedDate dateTime={max([startOfMonth(dateTime), startDateTime])} />
                                &ndash;
                                <FormattedDate dateTime={min([endOfMonth(dateTime), endDateTime])} />
                            </>
                        )}
                    </>
                ) : (
                    <FormattedDate dateTime={dateTime} />
                )}
            </div>
            {timePeriodGroup === TimePeriodGroupBy.Minutes && (
                <div className="phrase-counts-chart-tooltip__time">
                    {format(dateTime, "p")} &ndash; {format(addMinutes(dateTime, 1), "p")}
                </div>
            )}
            {timePeriodGroup === TimePeriodGroupBy.Hours && (
                <div className="phrase-counts-chart-tooltip__time">
                    {format(dateTime, "p")} &ndash; {format(addHours(dateTime, 1), "p")}
                </div>
            )}
            {timePeriodGroup === TimePeriodGroupBy.Days && (
                <div className="phrase-counts-chart-tooltip__time">
                    <FormattedDate dateTime={dateTime} />
                </div>
            )}
            <div className="phrase-counts-chart-tooltip__value">
                {proportion ? <>{(value * 100).toFixed(1)}%</> : value.toLocaleString()}
            </div>
            interactions
        </div>
    );
};

export default memo(PhraseCountsChartTooltip);
